import type { Edge, Node } from '@xyflow/react';
import { VALID_ACTION_TYPES } from './workflowConstants';

export const SUPPORTED_CANVAS_NODE_TYPES = ['action', 'loop', 'if-else', 'note', 'start'] as const;
export type SupportedCanvasNodeType = typeof SUPPORTED_CANVAS_NODE_TYPES[number];

const LEGACY_CANVAS_NODE_TYPE_MAP: Record<string, SupportedCanvasNodeType> = {
  ifElse: 'if-else',
  'if-else': 'if-else',
  action: 'action',
  loop: 'loop',
  note: 'note',
  start: 'start',
};

export interface WorkflowGraphIssue {
  type: 'cross-scope-edge' | 'if-else-join' | 'branch-to-root-edge';
  message: string;
  edgeId?: string;
  ifElseNodeId?: string;
  nodeIds: string[];
}

export function normalizeCanvasNodeType(rawType: unknown): SupportedCanvasNodeType | null {
  if (typeof rawType !== 'string') {
    return null;
  }

  if (rawType in LEGACY_CANVAS_NODE_TYPE_MAP) {
    return LEGACY_CANVAS_NODE_TYPE_MAP[rawType];
  }

  return null;
}

export function inferCanvasNodeType(
  dataType: unknown,
  configType: unknown
): SupportedCanvasNodeType | null {
  const directType = normalizeCanvasNodeType(dataType) ?? normalizeCanvasNodeType(configType);
  if (directType) {
    return directType;
  }

  if (
    typeof dataType === 'string' &&
    (VALID_ACTION_TYPES as readonly string[]).includes(dataType)
  ) {
    return 'action';
  }

  if (
    typeof configType === 'string' &&
    (VALID_ACTION_TYPES as readonly string[]).includes(configType)
  ) {
    return 'action';
  }

  return null;
}

export function collectBranchNodeIds(
  startIds: string[],
  nodeMap: Map<string, Node>,
  allEdges: Edge[],
  excludeIds: Set<string> = new Set(),
  /** parentId del nodo IF/ELSE — solo recolectar nodos en el mismo scope */
  scopeParentId?: string
): string[] {
  const collected: string[] = [];
  const visited = new Set<string>();
  const queue = [...startIds];

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    if (visited.has(currentId) || excludeIds.has(currentId)) {
      continue;
    }

    const currentNode = nodeMap.get(currentId);
    const currentType = currentNode ? normalizeCanvasNodeType(currentNode.type) : null;
    // Solo recolectar nodos del mismo scope (mismo parentId que el IF/ELSE)
    if (!currentNode || currentNode.parentId !== scopeParentId || !currentType || currentType === 'note') {
      continue;
    }

    visited.add(currentId);
    collected.push(currentId);

    // H-F029: Filtrar edges con sourceHandle (como 'true'/'false') porque esas son
    // edges de salida de nodos if-else hacia sus ramas, no edges secuenciales normales.
    // Solo seguimos edges sin sourceHandle para recolectar nodos de una rama lineal.
    allEdges
      .filter((edge) => edge.source === currentId && !edge.sourceHandle)
      .forEach((edge) => {
        if (!visited.has(edge.target)) {
          queue.push(edge.target);
        }
      });
  }

  return collected;
}

function getIfAfterStartIds(ifNodeId: string, edges: Edge[]): string[] {
  return edges
    .filter((edge) => edge.source === ifNodeId && edge.sourceHandle === 'after')
    .map((edge) => edge.target);
}

/**
 * Devuelve un mapa nodeId → 'true' | 'false' indicando a qué rama pertenece
 * cada nodo de un IF/ELSE del nivel raíz.
 *
 * Si un nodo aparece en la rama TRUE, se registra como 'true'.
 * Los nodos de la rama FALSE se registran como 'false'.
 * Los nodos fuera de cualquier rama no aparecen en el mapa.
 *
 * Útil para mostrar badges visuales y colorear edges por rama.
 */
export function computeBranchNodeMap(nodes: Node[], edges: Edge[]): Map<string, 'true' | 'false'> {
  const nodeMap = new Map<string, Node>(nodes.map((n) => [n.id, n]));
  const branchMap = new Map<string, 'true' | 'false'>();

  // Buscar nodos IF/ELSE en cualquier nivel (raíz y dentro de loops)
  const ifElseNodes = nodes.filter((n) => normalizeCanvasNodeType(n.type) === 'if-else');

  ifElseNodes.forEach((ifNode) => {
      const trueStartIds = edges
        .filter((e) => e.source === ifNode.id && e.sourceHandle === 'true')
        .map((e) => e.target);
      const falseStartIds = edges
        .filter((e) => e.source === ifNode.id && e.sourceHandle === 'false')
        .map((e) => e.target);
      const afterStartIds = getIfAfterStartIds(ifNode.id, edges);

      // Pasar el parentId del IF/ELSE para recolectar solo nodos del mismo scope
      const scope = ifNode.parentId;

      const trueIds = collectBranchNodeIds(
        trueStartIds,
        nodeMap,
        edges,
        new Set(afterStartIds),
        scope
      );
      trueIds.forEach((id) => { if (!branchMap.has(id)) branchMap.set(id, 'true'); });

      const falseIds = collectBranchNodeIds(
        falseStartIds,
        nodeMap,
        edges,
        new Set([...trueIds, ...afterStartIds]),
        scope
      );
      falseIds.forEach((id) => { if (!branchMap.has(id)) branchMap.set(id, 'false'); });
    });

  return branchMap;
}

/**
 * Devuelve el conjunto de IDs de todos los nodos que pertenecen a alguna rama
 * (true o false) de cualquier nodo if-else del nivel raíz.
 *
 * Derivado de computeBranchNodeMap para mantener compatibilidad.
 */
export function computeAllBranchNodeIds(nodes: Node[], edges: Edge[]): Set<string> {
  return new Set(computeBranchNodeMap(nodes, edges).keys());
}

export function analyzeWorkflowGraph(nodes: Node[], edges: Edge[]): WorkflowGraphIssue[] {
  const issues: WorkflowGraphIssue[] = [];
  const nodeMap = new Map<string, Node>(nodes.map((node) => [node.id, node]));
  const seenKeys = new Set<string>();

  const pushIssue = (issue: WorkflowGraphIssue) => {
    const issueKey = serializeWorkflowGraphIssue(issue);
    if (seenKeys.has(issueKey)) {
      return;
    }

    seenKeys.add(issueKey);
    issues.push(issue);
  };

  for (const edge of edges) {
    const sourceNode = nodeMap.get(edge.source);
    const targetNode = nodeMap.get(edge.target);
    if (!sourceNode || !targetNode) {
      continue;
    }

    const sourceScope = sourceNode.parentId ?? null;
    const targetScope = targetNode.parentId ?? null;
    if (sourceScope !== targetScope) {
      pushIssue({
        type: 'cross-scope-edge',
        edgeId: edge.id,
        nodeIds: [edge.source, edge.target],
        message: `El edge ${edge.id} cruza scopes incompatibles`,
      });
    }
  }

  nodes
    .filter((node) => normalizeCanvasNodeType(node.type) === 'if-else')
    .forEach((ifNode) => {
      const trueStartIds = edges
        .filter((edge) => edge.source === ifNode.id && edge.sourceHandle === 'true')
        .map((edge) => edge.target);
      const falseStartIds = edges
        .filter((edge) => edge.source === ifNode.id && edge.sourceHandle === 'false')
        .map((edge) => edge.target);
      const afterStartIds = getIfAfterStartIds(ifNode.id, edges);

      const scope = ifNode.parentId;
      const trueBranchIds = collectBranchNodeIds(
        trueStartIds,
        nodeMap,
        edges,
        new Set(afterStartIds),
        scope
      );
      const falseBranchIds = collectBranchNodeIds(
        falseStartIds,
        nodeMap,
        edges,
        new Set(afterStartIds),
        scope
      );
      const falseBranchSet = new Set(falseBranchIds);
      const sharedNodeIds = [...new Set(trueBranchIds.filter((nodeId) => falseBranchSet.has(nodeId)))];

      if (sharedNodeIds.length > 0) {
        pushIssue({
          type: 'if-else-join',
          ifElseNodeId: ifNode.id,
          nodeIds: sharedNodeIds.sort(),
          message: `El if/else ${ifNode.id} tiene joins no soportados`,
        });
      }

      // Detectar edges que salen de cualquier nodo de rama hacia un nodo fuera de la rama
      // (subcaso: nodo de rama → nodo raíz). Se evalúa sobre los edges ya existentes;
      // la prevención en tiempo de conexión se hace adicionalmente en onConnect.
      const allBranchIdsForIf = new Set([...trueBranchIds, ...falseBranchIds]);
      for (const edge of edges) {
        // Solo edges normales de nodos de rama (no handles especiales del IF)
        if (edge.sourceHandle) continue;
        if (!allBranchIdsForIf.has(edge.source)) continue;
        if (allBranchIdsForIf.has(edge.target)) continue;
        pushIssue({
          type: 'branch-to-root-edge',
          ifElseNodeId: ifNode.id,
          nodeIds: [edge.source, edge.target],
          message: `El nodo ${edge.source} está en una rama del if/else y conecta hacia el flujo raíz`,
        });
      }
    });

  return issues;
}

export function serializeWorkflowGraphIssue(issue: WorkflowGraphIssue): string {
  if (issue.type === 'cross-scope-edge') {
    return `cross-scope:${issue.edgeId ?? issue.nodeIds.join('->')}`;
  }

  if (issue.type === 'branch-to-root-edge') {
    return `branch-to-root:${issue.nodeIds.join('->')}`;
  }

  return `if-else-join:${issue.ifElseNodeId ?? 'unknown'}:${issue.nodeIds.join(',')}`;
}
